import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, XCircle, RefreshCw, Send, Power, ChevronRight, X } from 'lucide-react';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useUpgradeFlow } from '../../hooks/useUpgradeFlow';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

interface KudosityStatus {
  connected: boolean;
  senderId?: string;
  isActive?: boolean;
  lastUsedAt?: string;
}

export const KudosityIntegration: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { handleApiError, openUpgradeFlow } = useUpgradeFlow();
  const [status, setStatus] = useState<KudosityStatus>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testPhone, setTestPhone] = useState('');

  const [setupData, setSetupData] = useState({
    apiKey: '',
    apiSecret: '',
    senderId: ''
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Refetch when company/account changes so status reflects the correct company
  useEffect(() => {
    fetchStatus();
  }, [user?.currentAccountId]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiUrl}/api/sms/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      // Explicitly treat errors or missing company as not connected
      if (!data.success) {
        setStatus({ connected: false });
        if (data.message) setError(data.message);
        return;
      }

      // Only show as connected if we have a Kudosity integration for this company
      if (data.data?.connected && data.data?.provider === 'KUDOSITY') {
        setStatus({
          connected: true,
          senderId: data.data.senderId,
          isActive: data.data.isActive,
          lastUsedAt: data.data.lastUsedAt
        });
      } else {
        setStatus({ connected: false });
      }
    } catch (err) {
      console.error('Fetch Kudosity status error:', err);
      setError('Failed to connect to server');
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/sms/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: 'KUDOSITY',
          apiKey: setupData.apiKey,
          apiSecret: setupData.apiSecret,
          senderId: setupData.senderId
        })
      });

      const data = await response.json();

      if (data.success) {
        // Check if requiresUpgrade flag is set
        if (data.data?.requiresUpgrade) {
          setSuccess('Kudosity credentials saved. Click "Enable Integration" to activate.');
          setShowSetupModal(false);
          fetchStatus();
          resetSetupData();
        } else {
        setSuccess('Kudosity configured successfully!');
        setShowSetupModal(false);
        setShowTestModal(true); // Open test modal after successful setup
        fetchStatus();
        resetSetupData();
        }
      } else {
        setError(data.message || 'Failed to configure Kudosity');
      }
    } catch (err) {
      console.error('Setup Kudosity error:', err);
      setError('Failed to configure Kudosity');
    }
  };

  const handleTest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/sms/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber: testPhone })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Test SMS sent successfully! Check your phone.');
        setShowTestModal(false);
        setTestPhone('');
      } else {
        setError(data.message || 'Failed to send test SMS');
      }
    } catch (err) {
      console.error('Test SMS error:', err);
      setError('Failed to send test SMS');
    }
  };

  const handleEnable = async () => {
    try {
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/sms/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      // Check if this is a limit error and show upgrade modal
      const wasLimitError = handleApiError(response, data);
      if (wasLimitError) {
        return;
      }

      if (data.success) {
        setSuccess('Kudosity activated successfully!');
        fetchStatus();
      } else {
        setError(data.message || 'Failed to activate Kudosity');
      }
    } catch (err) {
      console.error('Activate Kudosity error:', err);
      setError('Failed to activate Kudosity');
    }
  };

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/sms/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Kudosity disconnected successfully');
        fetchStatus();
      } else {
        setError(data.message || 'Failed to disconnect Kudosity');
      }
    } catch (err) {
      console.error('Disconnect Kudosity error:', err);
      setError('Failed to disconnect Kudosity');
    } finally {
      setShowDisconnectConfirm(false);
    }
  };

  const resetSetupData = () => {
    setSetupData({
      apiKey: '',
      apiSecret: '',
      senderId: ''
    });
  };

  return (
    <>
      {/* Compact Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6 h-full flex flex-col">
        <div className="mb-4">
          <div className="flex items-start mb-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 transition-colors ${
              status.connected 
                ? 'bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50' 
                : 'bg-gray-100 dark:bg-gray-800'
            }`}>
              <MessageSquare className="w-8 h-8 text-accent-600 dark:text-accent-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Kudosity
              </h3>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                  {t('admin.kudosityDescription')}
                </p>
                {status.connected ? (
                  status.isActive ? (
                    <div className="flex items-center text-success-600 dark:text-success-400 flex-shrink-0">
                      <CheckCircle className="w-4 h-4 mr-1.5" />
                      <span className="font-medium text-sm">{t('admin.active')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-warning-600 dark:text-warning-400 flex-shrink-0">
                      <Power className="w-4 h-4 mr-1.5" />
                      <span className="font-medium text-sm">{t('admin.inactive')}</span>
                    </div>
                  )
                ) : (
                  <div className="flex items-center text-gray-500 dark:text-gray-400 flex-shrink-0">
                    <XCircle className="w-4 h-4 mr-1.5" />
                    <span className="font-medium text-sm">{t('admin.notConnected')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-800 dark:text-success-400">{success}</p>
          </div>
        )}

        <div className="mt-auto pt-4">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
            </div>
          ) : status.connected ? (
            <div className="flex gap-2">
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-error-500 text-white rounded-lg hover:bg-error-600 transition-colors font-medium text-sm"
              >
                {t('admin.disconnect')}
              </button>
              <button
                onClick={() => setShowDetailModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-700 dark:text-gray-300 font-medium text-sm"
              >
{t('admin.info')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setShowSetupModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium text-sm"
              >
                {t('admin.connect')}
              </button>
              <button
                onClick={() => setShowDetailModal(true)}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-700 dark:text-gray-300 font-medium text-sm"
              >
{t('admin.info')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.kudosityDetails')}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {status.connected ? (
                <>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {status.senderId && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">{t('admin.senderId')}:</span>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{status.senderId}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">{t('admin.status')}:</span>
                        <p className={`font-medium ${status.isActive ? 'text-success-600 dark:text-success-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {status.isActive ? t('admin.active') : t('admin.inactive')}
                        </p>
                      </div>
                      {status.lastUsedAt && (
                        <div className="col-span-2">
                          <span className="text-gray-600 dark:text-gray-400">{t('admin.lastUsed')}:</span>
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {new Date(status.lastUsedAt).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {!status.isActive && (
                    <div className="p-3 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <p className="text-sm text-warning-800 dark:text-warning-200">
                        {t('admin.kudositySavedInactive')}
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                    {!status.isActive ? (
                      <button
                        onClick={handleEnable}
                        className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
                      >
                        <Power size={18} />
                        Enable Integration
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setShowDetailModal(false);
                          setShowTestModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 text-accent-600 dark:text-accent-400 rounded-lg hover:bg-accent-100 dark:hover:bg-accent-900/30 transition-colors"
                      >
                        <Send size={18} />
                        Send Test SMS
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setShowSetupModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <RefreshCw size={18} />
                      Reconfigure
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setShowDisconnectConfirm(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 text-error-600 dark:text-error-400 rounded-lg hover:bg-error-100 dark:hover:bg-error-900/30 transition-colors"
                    >
                      <XCircle size={18} />
                      {t('admin.disconnect')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.kudosityNoIntegration')}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('admin.kudosityNoIntegrationHint')}
                    </p>
                  </div>
                  <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setShowSetupModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      <MessageSquare size={18} />
                      {t('admin.connect')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Configure Kudosity</h2>
            </div>

            <form onSubmit={handleSetup} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key *
                </label>
                <input
                  type="text"
                  required
                  value={setupData.apiKey}
                  onChange={(e) => setSetupData({ ...setupData, apiKey: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Secret *
                </label>
                <input
                  type="password"
                  required
                  value={setupData.apiSecret}
                  onChange={(e) => setSetupData({ ...setupData, apiSecret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sender ID *
                </label>
                <input
                  type="text"
                  required
                  value={setupData.senderId}
                  onChange={(e) => setSetupData({ ...setupData, senderId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., YourCompany"
                  maxLength={11}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Required. Use your registered sender ID from Kudosity (max 11 characters).
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowSetupModal(false);
                    resetSetupData();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
                >
                  Configure
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Test SMS Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Send Test SMS</h2>
            </div>

            <form onSubmit={handleTest} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="+61400000000"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Include country code (e.g., +61 for Australia, +64 for NZ)
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowTestModal(false);
                    setTestPhone('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
                >
                  Send Test
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation */}
      <ConfirmDialog
        isOpen={showDisconnectConfirm}
        onCancel={() => setShowDisconnectConfirm(false)}
        onConfirm={handleDisconnect}
        title={t('admin.disconnectKudosity')}
        message={t('admin.disconnectKudosityMessage')}
        confirmLabel={t('admin.disconnect')}
        cancelLabel={t('common.cancel')}
      />
    </>
  );
};

